'use client';

import {
  useState,
  useEffect,
  FC,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getCookie, setCookie } from 'cookies-next';
import { TAX_YEARS, CURRENT_TAX_YEAR } from '@/_constants/year-options';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UploadTaxReturnDialogProps } from './UploadTaxReturnDialog.types';
import { ToastContext } from '@/_contexts/ToastProvider';
import { TAX_RETURN_DELIVERY_PATH } from '@/_constants/tax-return';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { uploadTaxReturn } from '@/_api/admin-tax-returns';
import {
  Icon,
  Pill,
  Flex,
  File,
  Dialog,
  UploadBox,
  Select,
  Button,
  ButtonGroup,
  EntitySelectorInputChips,
  Separator,
  Checkbox,
  Text,
  Alert,
} from '@/_components';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import {
  DELIVERY_DRAFT_LINK,
  TAX_SUMMARY_LINK,
} from '@/_constants/help-center';
import { validateFile } from '@/_utils/files';
import { getAdminEntitiesByIds } from '@/_api/v2/admin-entities';

export const UploadTaxReturnDialog: FC<UploadTaxReturnDialogProps> = ({
  onUploaded,
}) => {
  const { token } = useContext(UserContext);
  const { refreshCompanyTaxReturns, selectedCompany } =
    useContext(CompanyContext);
  const { loadQuestionnaireBacklogCount } = useContext(QuestionnaireContext);
  const { preferences } = useContext(FirmSettingsContext);

  const { addToast, resetToasts } = useContext(ToastContext);
  const {
    isSending,
    setSending,
    selectedTaxReturnForReplacement,
    setSelectedTaxReturnForReplacement,
  } = useContext(AdminTaxReturnsContext);

  const onClose = useCallback(() => setSending(false), [setSending]);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [taxYear, setTaxYear] = useState<string>(CURRENT_TAX_YEAR);
  const [isDraft, setIsDraft] = useState<boolean>(false);
  const [isTaxSummaryChecked, setIsTaxSummaryEnabled] = useState<boolean>(
    () => {
      const savedValue = getCookie('taxSummaryChecked');
      return savedValue === 'true';
    },
  );
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Array<number>>(
    [],
  );
  const [isIndividualEntity, setIsIndividualEntity] = useState<boolean>(false);

  useEffect(() => {
    if (!isSending) {
      resetToasts();
      setSelectedFiles([]);
      setLoading(false);
      setIsDraft(false);
      setSelectedTaxReturnForReplacement(undefined);
      setSelectedCompanyIds([]);
    } else {
      if (selectedTaxReturnForReplacement?.id) {
        setSelectedCompanyIds([selectedTaxReturnForReplacement.company_id]);
        setTaxYear(selectedTaxReturnForReplacement.tax_year.toString());
        setIsDraft(true);
      }
    }
  }, [
    isSending,
    resetToasts,
    selectedTaxReturnForReplacement,
    setSelectedTaxReturnForReplacement,
  ]);

  // Preselect the selected company
  useEffect(() => {
    if (selectedCompany?.id && isSending) {
      setSelectedCompanyIds([selectedCompany?.id]);
    }
  }, [selectedCompany, isSending]);

  const onDrop = useCallback(
    async (acceptedFiles: Array<File>) => {
      // Filter to only allow PDF files
      const pdfFiles = acceptedFiles.filter(
        (file) =>
          file.type === 'application/pdf' ||
          file.name.toLowerCase().endsWith('.pdf'),
      );

      if (pdfFiles.length !== acceptedFiles.length) {
        addToast('error', 'Only PDF files are allowed for combination.');
      }

      setSelectedFiles((prev) => [...prev, ...pdfFiles]);
    },
    [addToast],
  );

  const onYearChange = useCallback(
    (event: any) => setTaxYear(event.target.value),
    [],
  );

  const removeFile = useCallback((indexToRemove: number) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSelectedFiles((currentFiles) => {
        const oldIndex = currentFiles.findIndex(
          (_, index) => index.toString() === active.id,
        );
        const newIndex = currentFiles.findIndex(
          (_, index) => index.toString() === over?.id,
        );

        return arrayMove(currentFiles, oldIndex, newIndex);
      });
    }
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const isUploadable: boolean = useMemo(
    () =>
      (selectedCompanyIds?.length === 1 ||
        !!selectedTaxReturnForReplacement?.id) &&
      selectedFiles.length > 0 &&
      !loading,
    [
      selectedCompanyIds,
      selectedFiles,
      loading,
      selectedTaxReturnForReplacement,
    ],
  );

  useEffect(() => {
    if (selectedCompany?.entity_type === 'Individual') {
      setIsIndividualEntity(true);
      return;
    }

    if (token && selectedCompanyIds.length === 1) {
      getAdminEntitiesByIds({
        token,
        ids: selectedCompanyIds,
      }).then((entities) => {
        if (entities?.[0]?.entity_type === 'Individual') {
          setIsIndividualEntity(true);
        } else {
          setIsIndividualEntity(false);
        }
      });
    } else {
      setIsIndividualEntity(false);
    }
  }, [token, selectedCompany, selectedCompanyIds]);

  const isTaxSummaryEnabled = useMemo((): boolean => {
    return (
      preferences?.tax_return_tax_summary_enabled === true &&
      isIndividualEntity &&
      isTaxSummaryChecked &&
      !isDraft
    );
  }, [preferences, isIndividualEntity, isTaxSummaryChecked, isDraft]);

  const onUpload = useCallback(async (): Promise<void> => {
    if (!isUploadable) {
      return;
    }

    try {
      setLoading(true);

      // Validate all files
      const validationPromises = selectedFiles.map((file) =>
        validateFile(file),
      );
      const validationResults = await Promise.all(validationPromises);

      const hasProtectedPDF = validationResults.some(
        (result) => result.isProtectedPDF,
      );

      if (hasProtectedPDF) {
        return addToast(
          'error',
          'One or more PDFs are password-protected or cannot be opened. Please either remove the password or upload alternative files.',
        );
      }

      const path = `${TAX_RETURN_DELIVERY_PATH}/${taxYear}`;

      try {
        await uploadTaxReturn({
          companyId: selectedCompanyIds?.[0],
          files: selectedFiles,
          path,
          documentName: selectedFiles[0]?.name,
          taxYear: parseInt(taxYear),
          sendDraftForApproval: isDraft,
          taxSummaryEnabled: isTaxSummaryEnabled,
          previousTaxReturnId: selectedTaxReturnForReplacement?.id,
          token,
        });
        refreshCompanyTaxReturns();
        onClose();

        if (onUploaded) {
          // For multiple files, we'll pass the first file and path for backward compatibility
          onUploaded(selectedFiles[0], path);
        }
      } catch (err: any) {
        // Show a user-visible error so they know the upload failed
        addToast(
          'error',
          err?.message || 'File upload failed, please try again.',
        );
      }
    } catch (error) {
      return addToast('error', 'File upload failed, please try again.', {
        error,
      });
    } finally {
      setLoading(false);
      setTimeout(() => {
        loadQuestionnaireBacklogCount();
      }, 100);
    }
  }, [
    loadQuestionnaireBacklogCount,
    selectedFiles,
    selectedCompanyIds,
    taxYear,
    token,
    onClose,
    addToast,
    isUploadable,
    onUploaded,
    refreshCompanyTaxReturns,
    isTaxSummaryEnabled,
    isDraft,
    selectedTaxReturnForReplacement,
  ]);

  const onChipsChange = (chips: Array<number>) => setSelectedCompanyIds(chips);

  const handleDeliveryDraftLink = () => {
    window.open(DELIVERY_DRAFT_LINK, '_blank');
  };

  const handleTaxSummaryLink = () => {
    window.open(TAX_SUMMARY_LINK, '_blank');
  };

  const SortableFileItem = ({
    file,
    index,
    onRemove,
    isDisabled,
  }: {
    file: File;
    index: number;
    onRemove: () => void;
    isDisabled: boolean;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: index.toString(), disabled: isDisabled });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.8 : 1,
    };

    return (
      <div ref={setNodeRef} style={style}>
        <File title={file.name}>
          <Flex gap={2} isYCentered>
            <div
              {...attributes}
              {...listeners}
              style={{
                cursor: isDisabled ? 'not-allowed' : 'grab',
                padding: '4px',
                opacity: isDisabled ? 0.5 : 1,
              }}
            >
              <Icon name="dots-six-vertical" size={20} />
            </div>

            <Button
              isDisabled={isDisabled}
              intent="danger-secondary"
              value="Remove"
              size="tiny"
              isIconOnly={true}
              iconRight="trash"
              onClick={onRemove}
            />
          </Flex>
        </File>
      </div>
    );
  };

  return (
    <Dialog
      isOpen={isSending}
      isNarrow={true}
      title={
        selectedTaxReturnForReplacement
          ? 'Replace Tax Return'
          : 'Upload Tax Return'
      }
      onClose={onClose}
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={6}>
          <EntitySelectorInputChips
            label="Select Entity"
            preselectedEntityIds={
              selectedCompany?.id
                ? [selectedCompany?.id]
                : selectedTaxReturnForReplacement?.company_id
                  ? [selectedTaxReturnForReplacement.company_id]
                  : []
            }
            onChipsChange={onChipsChange}
            isSingleSelection={true}
            isDisabled={!!selectedTaxReturnForReplacement}
          />
          <UploadBox
            onDrop={onDrop}
            isLoading={loading}
            allowMultiple
            accept=".pdf"
          />

          {selectedFiles.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedFiles.map((_, index) => index.toString())}
                strategy={verticalListSortingStrategy}
              >
                <Flex isColumn={true} gap={2}>
                  {selectedFiles.map((file, index) => (
                    <SortableFileItem
                      key={`${file.name}-${file.size}-${index}`}
                      file={file}
                      index={index}
                      onRemove={() => removeFile(index)}
                      isDisabled={loading}
                    />
                  ))}
                </Flex>
              </SortableContext>
            </DndContext>
          )}

          {selectedFiles.length > 1 && (
            <Alert type="info" icon="info">
              Important: {selectedFiles.length} files will be combined into a
              single PDF for delivery in the order shown above.
            </Alert>
          )}

          <Select
            label="Tax year"
            items={TAX_YEARS}
            selectedValue={taxYear}
            onChange={onYearChange}
            isDisabled={!!selectedTaxReturnForReplacement}
          />

          {preferences?.tax_return_tax_summary_enabled ? (
            <Flex isColumn={true}>
              <Flex isBetween={true}>
                <Flex gap={4} isYCentered>
                  <Checkbox
                    label="Enable Tax Summary"
                    isChecked={isTaxSummaryEnabled}
                    isDisabled={!isIndividualEntity || isDraft}
                    onChange={() => {
                      const newValue = !isTaxSummaryChecked;
                      setIsTaxSummaryEnabled(newValue);
                      setCookie('taxSummaryChecked', newValue.toString());
                    }}
                  />
                  <Pill intent="info" label="Beta" />
                </Flex>
                <Button
                  value="How it works"
                  intent="ghost"
                  size="small"
                  iconLeft="book-open-text"
                  onClick={handleTaxSummaryLink}
                />
              </Flex>
              {isIndividualEntity === false ? (
                <Text type="tiny" color="secondary">
                  This option is only available for &quot;Individual&quot;
                  entities.
                </Text>
              ) : (
                <Text type="tiny" color="secondary">
                  If checked, the system automatically extracts &quot;Amount
                  Refunded&quot; and &quot;Amount Owe&quot; from the tax return.
                </Text>
              )}
            </Flex>
          ) : null}

          <Separator isLight={true} />

          <Flex isColumn={true}>
            <Flex isBetween={true}>
              <Checkbox
                label="Send draft for Approval"
                isChecked={isDraft}
                isDisabled={isTaxSummaryEnabled}
                onChange={() => setIsDraft(!isDraft)}
              />
              <Button
                value="How it works"
                intent="ghost"
                size="small"
                iconLeft="book-open-text"
                onClick={handleDeliveryDraftLink}
              />
            </Flex>
            <Text type="tiny" color="secondary">
              If checked, the tax return will be sent for approval by the
              primary contact of the entity with DRAFT watermark.
            </Text>
          </Flex>
          <ButtonGroup align="right">
            <Button
              value="Cancel"
              onClick={onClose}
              intent="danger-secondary"
            />
            <Button
              isDisabled={!isUploadable}
              value="Upload"
              onClick={onUpload}
              intent="primary"
              isLoading={loading}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
